import React, { useMemo, useState } from 'react';
import { Toast } from 'antd-mobile';
import { getAdminScenarioDetail, getAdminConversationSteps, AdminScenarioDetail } from '../../../lib/api/adminScenarios';
import type { ConversationStepResponse, AIRoleInScenario } from '../../../lib/api/scenario';

// 场景调试页面（PC端，Tailwind样式）
const ScenarioDebug: React.FC = () => {
  const [scenarioId, setScenarioId] = useState('');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<AdminScenarioDetail | null>(null);
  const [steps, setSteps] = useState<ConversationStepResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 选择复制的板块
  const [selected, setSelected] = useState({
    scenario: true,
    roles: true,
    steps: true,
    scoring: true,
  });

  const roles: AIRoleInScenario[] = useMemo(() => {
    if (!detail) return [] as AIRoleInScenario[];
    // 兼容可能的字段差异
    return (detail.ai_roles as AIRoleInScenario[]) || [];
  }, [detail]);

  const handleQuery = async () => {
    if (!scenarioId.trim()) {
      Toast.show('请输入场景ID');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [detailRes, stepsRes] = await Promise.all([
        getAdminScenarioDetail(scenarioId.trim()),
        getAdminConversationSteps(scenarioId.trim()),
      ]);

      // 处理详情响应
      let detailData: AdminScenarioDetail | null = null;
      if (detailRes && detailRes.success && (detailRes as any).data) {
        const d: any = (detailRes as any).data;
        detailData = (d && d.data) ? d.data : d;
      } else if ((detailRes as any)?.data) {
        const d: any = (detailRes as any).data;
        detailData = (d && d.data) ? d.data : d;
      }

      // 处理环节响应
      let stepsData: ConversationStepResponse[] = [];
      if (stepsRes && stepsRes.success && (stepsRes as any).data) {
        const s: any = (stepsRes as any).data;
        stepsData = (s && s.data) ? s.data : s;
      } else if ((stepsRes as any)?.data) {
        const s: any = (stepsRes as any).data;
        stepsData = (s && s.data) ? s.data : s;
      }

      if (!detailData) {
        throw new Error('未获取到场景详情');
      }

      setDetail(detailData);
      setSteps(stepsData || detailData.conversation_steps || []);
      Toast.show('查询成功');
    } catch (err: any) {
      const msg = err?.message || '查询失败';
      setError(msg);
      Toast.show(msg);
    } finally {
      setLoading(false);
    }
  };

  // 构建复制文本
  const buildCopyText = () => {
    const parts: string[] = [];

    if (selected.scenario && detail) {
      parts.push('【场景信息】');
      parts.push(`场景ID：${detail.id}`);
      parts.push(`场景名称：${detail.name || ''}`);
      parts.push(`场景描述：${detail.description || ''}`);
      parts.push(`标签：${(detail.tags || []).join('、')}`);
      parts.push(`关键考核点：${detail.key_points || ''}`);
      parts.push(`成绩要求：${detail.passing_score ?? ''}`);
      parts.push(`开场白：${detail.opening_dialogue || ''}`);
      parts.push(`角色总述：${detail.ai_roles_overall_description || ''}`);
      parts.push(`评分知识库：${(detail.scoring_knowledge_base_ids || []).join('、')}`);
    }

    if (selected.roles && roles && roles.length > 0) {
      parts.push('');
      parts.push('【角色信息】');
      roles.forEach((r, idx) => {
        const d = r.role_data || ({} as any);
        parts.push(`角色${idx + 1}：${d.name || ''}`);
        parts.push(`角色描述：${d.description || ''}`);
        parts.push(`角色背景：${d.background || ''}`);
        parts.push(`沟通风格：${d.communication_style || ''}`);
        parts.push(`性格类型：${d.personality_type || ''}`);
        parts.push(`知识水平：${(d.knowledge_level || []).join('、')}`);
        parts.push(`禁忌与异议：${(d.taboos_objections || []).join('、')}`);
        parts.push(`标签：${(d.tags || []).join('、')}`);
        parts.push(`语音ID：${d.voice_id || ''}`);
      });
    }

    if (selected.steps && steps && steps.length > 0) {
      parts.push('');
      parts.push('【环节信息】');
      steps
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .forEach((s, idx) => {
          parts.push(`环节${idx + 1}：${s.step_name}`);
          parts.push(`学员目标：${s.student_objective}`);
          parts.push(`AI任务：${s.ai_task}`);
          parts.push(`最大轮次：${s.max_rounds}`);
          parts.push(`排序：${s.sort_order}`);
        });
    }

    if (selected.scoring && detail && detail.scoring_criteria_config) {
      const cfg: any = detail.scoring_criteria_config;
      parts.push('');
      parts.push('【评分标准】');
      const name = cfg?.criteria_data?.name || cfg?.template_name || '';
      const desc = cfg?.criteria_data?.description || '';
      parts.push(`评分名称：${name}`);
      parts.push(`评分描述：${desc}`);
      const dims: any[] = cfg?.criteria_data?.dimensions || [];
      dims.forEach((dim, idx) => {
        parts.push(`维度${idx + 1}：${dim?.name || ''}`);
        parts.push(`权重：${dim?.weight ?? ''}`);
        parts.push(`评分规则：${dim?.scoring_rule || ''}`);
      });
    }

    return parts.join('\n');
  };

  const handleCopy = async () => {
    const text = buildCopyText();
    try {
      await navigator.clipboard.writeText(text);
      Toast.show('复制成功');
    } catch (e) {
      Toast.show('复制失败');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* 顶部操作栏 */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-xl font-bold">场景调试</h1>
        <input
          className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="请输入场景ID"
          value={scenarioId}
          onChange={(e) => setScenarioId(e.target.value)}
        />
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={loading}
          onClick={handleQuery}
        >
          {loading ? '查询中...' : '查询'}
        </button>
      </div>

      {/* 选择复制的内容 */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded border border-gray-200">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selected.scenario}
            onChange={(e) => setSelected((s) => ({ ...s, scenario: e.target.checked }))}
          />
          <span>场景信息</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selected.roles}
            onChange={(e) => setSelected((s) => ({ ...s, roles: e.target.checked }))}
          />
          <span>角色信息</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selected.steps}
            onChange={(e) => setSelected((s) => ({ ...s, steps: e.target.checked }))}
          />
          <span>环节信息</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selected.scoring}
            onChange={(e) => setSelected((s) => ({ ...s, scoring: e.target.checked }))}
          />
          <span>评分标准</span>
        </label>
        <div className="ml-auto">
          <button
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            disabled={!detail && steps.length === 0}
            onClick={handleCopy}
          >
            复制选定内容
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded">{error}</div>
      )}

      {/* 场景信息 */}
      {detail && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">场景信息</h2>
            <button
              className="px-3 py-1.5 bg-gray-800 text-white rounded text-sm"
              onClick={() => {
                const text = JSON.stringify(detail, null, 2);
                navigator.clipboard.writeText(text);
                Toast.show('场景信息JSON已复制');
              }}
            >
              复制JSON
            </button>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><span className="text-gray-500">场景ID：</span>{detail.id}</div>
            <div><span className="text-gray-500">名称：</span>{detail.name}</div>
            <div className="col-span-2"><span className="text-gray-500">描述：</span>{detail.description || '-'}</div>
            <div className="col-span-2"><span className="text-gray-500">标签：</span>{(detail.tags || []).join('、') || '-'}</div>
            <div className="col-span-2"><span className="text-gray-500">关键考核点：</span>{detail.key_points || '-'}</div>
            <div><span className="text-gray-500">成绩要求：</span>{detail.passing_score ?? '-'}</div>
            <div className="col-span-2"><span className="text-gray-500">开场白：</span>{detail.opening_dialogue || '-'}</div>
            <div className="col-span-2"><span className="text-gray-500">角色总述：</span>{detail.ai_roles_overall_description || '-'}</div>
          </div>
        </div>
      )}

      {/* 角色信息 */}
      {roles && roles.length > 0 && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">角色信息（{roles.length}）</h2>
            <button
              className="px-3 py-1.5 bg-gray-800 text-white rounded text-sm"
              onClick={() => {
                const text = JSON.stringify(roles, null, 2);
                navigator.clipboard.writeText(text);
                Toast.show('角色信息JSON已复制');
              }}
            >
              复制JSON
            </button>
          </div>
          <div className="space-y-3">
            {roles.map((r, idx) => (
              <div key={r.scenario_role_id || idx} className="p-3 border border-gray-100 rounded">
                <div className="font-medium mb-2">角色{idx + 1}：{r.role_data?.name || '-'}</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <div><span className="text-gray-500">描述：</span>{r.role_data?.description || '-'}</div>
                  <div><span className="text-gray-500">背景：</span>{r.role_data?.background || '-'}</div>
                  <div><span className="text-gray-500">沟通风格：</span>{r.role_data?.communication_style || '-'}</div>
                  <div><span className="text-gray-500">性格类型：</span>{r.role_data?.personality_type || '-'}</div>
                  <div className="col-span-2"><span className="text-gray-500">知识水平：</span>{(r.role_data?.knowledge_level || []).join('、') || '-'}</div>
                  <div className="col-span-2"><span className="text-gray-500">禁忌与异议：</span>{(r.role_data?.taboos_objections || []).join('、') || '-'}</div>
                  <div className="col-span-2"><span className="text-gray-500">标签：</span>{(r.role_data?.tags || []).join('、') || '-'}</div>
                  <div><span className="text-gray-500">语音ID：</span>{r.role_data?.voice_id || '-'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 环节信息 */}
      {steps && steps.length > 0 && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">环节信息（{steps.length}）</h2>
            <button
              className="px-3 py-1.5 bg-gray-800 text-white rounded text-sm"
              onClick={() => {
                const text = JSON.stringify(steps, null, 2);
                navigator.clipboard.writeText(text);
                Toast.show('环节信息JSON已复制');
              }}
            >
              复制JSON
            </button>
          </div>
          <div className="space-y-3">
            {steps
              .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
              .map((s, idx) => (
                <div key={s.id || idx} className="p-3 border border-gray-100 rounded">
                  <div className="font-medium mb-2">环节{idx + 1}：{s.step_name}</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div className="col-span-2"><span className="text-gray-500">学员目标：</span>{s.student_objective}</div>
                    <div className="col-span-2"><span className="text-gray-500">AI任务：</span>{s.ai_task}</div>
                    <div><span className="text-gray-500">最大轮次：</span>{s.max_rounds}</div>
                    <div><span className="text-gray-500">排序：</span>{s.sort_order}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* 评分标准 */}
      {detail?.scoring_criteria_config && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">评分标准</h2>
            <button
              className="px-3 py-1.5 bg-gray-800 text-white rounded text-sm"
              onClick={() => {
                const text = JSON.stringify(detail?.scoring_criteria_config, null, 2);
                navigator.clipboard.writeText(text);
                Toast.show('评分标准JSON已复制');
              }}
            >
              复制JSON
            </button>
          </div>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-500">评分名称：</span>{detail?.scoring_criteria_config?.criteria_data?.name || detail?.scoring_criteria_config?.template_name || '-'}</div>
            <div className="col-span-2"><span className="text-gray-500">评分描述：</span>{detail?.scoring_criteria_config?.criteria_data?.description || '-'}</div>
            <div className="mt-2 font-medium">评分维度：</div>
            <div className="space-y-2">
              {(detail?.scoring_criteria_config?.criteria_data?.dimensions || []).map((dim: any, idx: number) => (
                <div key={idx} className="p-2 border border-gray-100 rounded">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                    <div><span className="text-gray-500">名称：</span>{dim?.name || '-'}</div>
                    <div><span className="text-gray-500">权重：</span>{dim?.weight ?? '-'}</div>
                    <div className="col-span-2"><span className="text-gray-500">评分规则：</span>{dim?.scoring_rule || '-'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioDebug;